import every from 'lodash/every';
import filter from 'lodash/filter';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import moment from 'moment';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { hasRequiredTransportData } from '../fields/CarbonFootprintTransportField/utils/carbonFootprint.utils';
import MultiNumericField from '../fields/MultiNumericField';
import { hasValidRoomingData } from '../fields/RoomingField/utils';
import { evalCondition } from './conditions';
import { getEmailDomainRegex, validateEmail } from './emailUtils';
import { evalIfNeeded, isExpression } from './sanitizeData';
import { computeMinMaxCount } from '../components/utils';

function cleanObject(obj) {
  if (!obj) return {};
  return pickBy(obj, identity); // Remove falsey values
}

export function hasOriginalData(key) {
  return !isEmpty(get(window?.__DATA__?.data, key));
}

export function extractFields(formFields, options) {
  if (!formFields) return [];

  const fieldNames = [];

  function iterate(fields) {
    if (!fields) return;

    for (const field of fields) {
      if (field.name || options?.addAllFields) {
        fieldNames.push(field);
      }
      if (field.type !== 'ListItem' && field.type !== 'MultiNumericField') {
        iterate(field.fields);
      }
      iterate(field.tabs);
    }
  }

  iterate(formFields);
  return fieldNames;
}

export function extractCurrentFields(fields, data, parentData) {
  if (!fields) return [];
  let fieldNames = [];
  for (const field of fields) {
    if (evalCondition(field.condition, data) && (!parentData || evalCondition(field.parentCondition, parentData)) && !field.hidden) {
      if (field.name || field.type === 'MultiNumericField' || field.type === 'WorkshopRegistrationV2' || field.type === 'TownsField') {
        fieldNames.push(field);
      }
      if (field.fields && field.type !== 'ListItem' && field.type !== 'MultiNumericField') {
        fieldNames = [...fieldNames, ...extractCurrentFields(field.fields, data, parentData)];
      }
      if (field.tabs) {
        fieldNames = [...fieldNames, ...extractCurrentFields(field.tabs, data, parentData)];
      }
    }
  }
  return fieldNames;
}

export function extractRequiredFields(fields, data, parentData) {
  return extractCurrentFields(fields, data, parentData).filter(
    (f) =>
      (f.required ||
        f.type === 'ListItem' ||
        f.type === 'WorkshopRegistrationV2' ||
        f.type === 'RoomingField' ||
        f.type === 'CarbonFootprintTransportField') &&
      !f.disabled,
  );
}

export function validateListItems(listItemField, items, parentData) {
  const { fields } = listItemField;

  const minItems = computeMinMaxCount(listItemField.minItems, parentData);
  const maxItems = computeMinMaxCount(listItemField.maxItems, parentData);

  if (minItems && items.length < minItems) return false;
  if (maxItems && items.length > maxItems) return false;
  // console.log("validateListItems")

  for (const item of items) {
    if (!hasRequiredData(fields, item, parentData)) {
      return false;
    }
  }
  return true;
}

function isValidRequiredField(field, data) {
  switch (field.type) {
    case 'PhoneNumberField':
      const value = data[field.name];
      if (!value) return false;
      const stringValue = (Array.isArray(value) ? value.join('').replace('FR0', '+33') : value) || field.defaultValue;
      const isValidNumber = !stringValue || stringValue.length === 0 || isValidPhoneNumber(stringValue);
      if (!isValidNumber) return false;
      return true;
    case 'EmailField':
      const { name, whitelist = {}, blacklist = {} } = field;
      const email = data[name];
      if (!email) return false;
      const { domains: whitelistedDomains } = whitelist || {};
      const { domains: blacklistedDomains } = blacklist || {};
      const domainRegex = getEmailDomainRegex({ whitelist: whitelistedDomains, blacklist: blacklistedDomains });
      const { isValid } = validateEmail(email, domainRegex);
      if (!isValid) return false;
      return true;
    case 'DateSelectField': {
      const { name, format = 'YYYY-MM-DD' } = field;
      const date = data[name];
      if (!date) return false;
      return moment(date, format).isValid();
    }
    case 'ListItem': {
      const items = data[field.name] || [];
      return validateListItems(field, items, data);
    }
    case 'RoomingField': {
      return hasValidRoomingData(data[field.name], field.invitations, window.__DATA__?.data?._id);
    }
    case 'MultiSelectField': {
      const { minItems, maxItems, name } = field;
      const items = data[name] || [];
      if (minItems && items.length < minItems) return false;
      if (maxItems && items.length > maxItems) return false;
      return items.length > 0;
    }
    case 'MultiNumericField': {
      return MultiNumericField.validate(field, data);
    }
    case 'WorkshopRegistrationV2': {
      const { workshopsRegistrations = [] } = data;
      const { minCount, required } = field;
      const selectedWorkshops = workshopsRegistrations?.filter((registration) => field.workshops?.some((w) => w._id === registration.sessionId));
      return (!required || selectedWorkshops.length > 0) && (!minCount || selectedWorkshops.length >= Number(minCount));
    }
    case 'FlightField': {
      const { name, required } = field;
      const flightData = data[name];
      if (!required) return true;
      return flightData?.steps?.length > 0;
    }
    case 'CarbonFootprintTransportField': {
      return hasRequiredTransportData(data);
    }
    case 'TownsField': {
      const { cpName, townName } = field;
      const cpValue = data[cpName];
      const townValue = data[townName];
      return !!cpValue && !!townValue;
    }
    default: {
      return !!get(data, field.name);
    }
  }
}

// TODO: validate when parsing tree instead of extracting first :/ or memoize ?
function hasRequiredDataInFlatFields(fields, data) {
  return every(fields, (field) => isValidRequiredField(field, data));
}

export function hasRequiredData(fields, data, parentData) {
  return hasRequiredDataInFlatFields(extractRequiredFields(fields, data, parentData), data);
}

// Get the list of missing required fields with their names and labels
export function getMissingRequiredFields(fields, data, parentData) {
  const requiredFields = extractRequiredFields(fields, data, parentData);
  const missingFields = [];

  for (const field of requiredFields) {
    if (!isValidRequiredField(field, data)) {
      missingFields.push({
        name: field.name,
        label: field.label || field.name,
        type: field.type,
      });
    }
  }

  return missingFields;
}

// Form-wide constraint: minimum number of selected workshops across all
// WorkshopRegistrationV2 blocks. Set in the backoffice at `form.options.minWorkshopsCount`,
// exposed to the renderer as `window.__DATA__.options` (see computeFormOptions server-side).
// Returns the required minimum when unmet, otherwise null. Skipped if no block is visible.
export function getUnmetWorkshopsMinCount(fields, data) {
  const minCount = Number(window.__DATA__?.options?.minWorkshopsCount) || 0;
  if (!minCount) return null;

  const visibleWorkshopFields = extractCurrentFields(fields, data).filter((f) => f.type === 'WorkshopRegistrationV2');
  if (visibleWorkshopFields.length === 0) return null; // no visible block -> skip

  // Plain array + includes on purpose: building a Set/record never pays off for the handful
  // of registrations we check here, and measures ~2x slower at realistic sizes (tens of sessions).
  const workshopsIds = flatMap(visibleWorkshopFields, (f) => map(f.workshops || [], '_id'));
  const selectedCount = filter(data.workshopsRegistrations || [], (r) => workshopsIds.includes(r.sessionId)).length;

  return selectedCount < minCount ? minCount : null;
}

export function initializeData(form, params) {
  const fields = extractFields(form); //.map(f => f.name);
  const defaultData = (window.__DATA__ && window.__DATA__.data) || {};
  const defaultValues = keyBy(
    fields.filter((f) => f.hasOwnProperty('defaultValue') && !isExpression(f.defaultValue) && evalCondition(f.condition, defaultData)),
    'name',
  );
  const defaultParams = mapValues(defaultValues, (field) => evalIfNeeded(field.defaultValue, defaultData));
  if (!params) {
    params = {
      ...defaultParams,
      ...defaultData,
    };
  } else {
    params = {
      ...defaultParams,
      ...defaultData,
      ...cleanObject(params),
    };
  }
  // console.log("params", params)
  // const fieldNames = fields.map(f => f.name);
  return mapValues(params /*pick(params, fieldNames)*/, (v) => {
    if (v === 'true') return true;
    else if (v === 'false') return false;
    return v;
  });
}

export function hasProducts(fields) {
  // TODO : optimize (simple recursion and/or memoize ?)
  return !!extractFields(fields).find((f) => f.type === 'ProductField');
}

export function isUpdate() {
  return window.__DATA__ && window.__DATA__.isUpdate;
}

export function isServiceV2() {
  return window.__DATA__?.api?.serviceVersion === 'v2';
}

export function getDefaultData() {
  return window.__DATA__?.data || {};
}
